import { analyticsManager, useErrorHelper, useMessageDisplayer } from '@inpart-io/devtools-vue';
import { type IPrivateEmail } from '@inpart-io/padme-iap';
import { useQuasar } from 'quasar';
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';

import DialogConfirm from '@/components/dialog/dialog-confirm.vue';
import { ANALYTICS_EVENTS } from '@/constants/analytics.constants';
import { ICONS_NAME } from '@/constants/ui/icons.constants';
import { i18nService } from '@/services/i18n-service';
import { CASCADE_DELETE_FILE } from '@/store/base/binary-uploads/actions';
import { REMOVE_SEARCH_RESULT } from '@/store/base/search/actions';
import { PRIVATE_EMAIL_MODULE } from '@/store/private-email-module';

export const DEFAULT_EMAIL_SUBJECT = `[${i18nService.t('NO_SUBJECT')}]`;

export function useEmailHelper(): {
    getEmailFrom: (email: IPrivateEmail) => string;
    getEmailLabel: (email: IPrivateEmail, extension?: boolean) => string;
    removeEmail: (key: string, emailId: number, isMeeting?: boolean) => Promise<void>;
} {
    const store = useStore();
    const { t } = useI18n();
    const q = useQuasar();
    const loading = ref(false);
    const messageDisplayer = useMessageDisplayer();
    const errorHelper = useErrorHelper();

    function getEmailFrom(email: IPrivateEmail): string {
        return (email?.from || '').replace(/<.*?>/, '');
    }
    function getEmailLabel(email: IPrivateEmail, extension = true): string {
        if (!extension) {
            const extensionIndex = email?.label.lastIndexOf('.');
            return email?.label.slice(0, extensionIndex);
        }
        return email?.label;
    }

    async function removeEmail(key: string, emailId: number, isMeeting?: boolean): Promise<void> {
        return new Promise((resolve) => {
            q.dialog({
                component: DialogConfirm,
                componentProps: {
                    title: t('ACTIONS.DELETE.TITLE', { object: 'email' }),
                    formWidth: '500',
                    iconName: ICONS_NAME.DELETE_OUTLINE,
                    negativeStyle: true,
                    confirmLabel: t('ACTION.DELETE').toString(),
                    message: t('ACTIONS.DELETE.CONFIRM', { object: 'email' })
                }
            }).onOk(() => {
                void (async () => {
                    try {
                        loading.value = true;
                        const analyticsEvent = isMeeting ? ANALYTICS_EVENTS.MEETINGS_MANAGEMENT.DELETED_EMAIL : ANALYTICS_EVENTS.EMAIL_IMPORT.DELETED;
                        analyticsManager.trackAction(analyticsEvent);
                        await store.dispatch(`${PRIVATE_EMAIL_MODULE}/${CASCADE_DELETE_FILE}`, { fileId: emailId });
                        await store.dispatch(`${PRIVATE_EMAIL_MODULE}/${REMOVE_SEARCH_RESULT}`, { key, idList: [emailId] });
                        messageDisplayer.success(t('NOTIFS.DELETE_SUCCESS', { object: 'Email' }).toString());
                    } catch (error) {
                        errorHelper.handleError(error, t('EMAIL.LOW.SING'));
                    } finally {
                        loading.value = false;
                        resolve();
                    }
                })();
            });
        });
    }

    return { getEmailFrom, getEmailLabel, removeEmail };
}
